// SEKVENSLAGET — ULLs eget lag over kontraktens `sekvensSpor()`.
//
// ══ HVA KONTRAKTEN GJØR, OG HVA SOM MANGLER ═════════════════════════════════
// `sekvensSpor()` i `@skade/contracts/ugs.ts` gjør regnestykket: gitt en LISTE av
// utløsninger som hører sammen, gir den bein, fartsINTERVALL, kurs med slark,
// antatt antall, `begrunnelse` og `advarsler`. Den er hovedfunksjonen i domenet,
// og ingenting av den logikken skal gjentas her.
//
// Men den svarer ikke på det første spørsmålet en jeger har: HVILKE utløsninger
// hører sammen? Broen leverer en STRØM av punkter fra hele feltet — noen fra en
// patrulje som gikk forbi for tre timer siden, noen fra en elg, noen fra det som
// skjer NÅ. Grupperingen er derfor denne filas jobb, og den er en TOLKNING:
// grupperer man feil, får man et pent fartstall for to ulike mål.
//
// ══ GRUPPERINGSREGLENE, OG HVORFOR DE ER SÅ FÅ ═════════════════════════════
// Kandidatene deles på TO kriterier, og bare to:
//
//   1. TID. Er oppholdet til forrige utløsning større enn `maksOppholdMs`
//      (kontraktens `MAKS_OPPHOLD_MS`, 5 min), er det to hendelser. Dette er
//      SAMME terskel kontrakten selv bruker til å flagge `bruddIKjede`, og det er
//      med vilje: da slipper vi å levere kontrakten en kjede den bare kommer til
//      å fortelle oss er brutt.
//
//   2. AVSTAND, med et ROMSLIG tak (`MAKS_SPRANG_M`). To utløsninger 1,5 km fra
//      hverandre tilhører ulike deler av feltet, og å ha dem i samme kandidat
//      ville gjort netto kurs meningsløs.
//
// LEGG MERKE TIL HVA VI IKKE DELER PÅ: fysisk umulig fart. Det ville vært
// forførende — «26 m/s til fots, altså to ulike mål, altså to kandidater» — men
// det er nettopp den slutningen jegeren skal få se, ikke en vi skal gjøre for
// ham. Kontraktens `urimeligFart` sier «minst to ulike mål, ELLER minst én klokke
// er feil», og de to krever helt ulik handling. Deler vi stille, forsvinner
// valget. Umulige bein blir derfor stående INNE i kandidaten, flagget.
//
// ══ HVA SOM ER PÅKREVD AV UI-ET, OG HÅNDHEVES HERFRA ═══════════════════════
// `retningsutsagn()` er skrevet for å gjøre det UMULIG å tegne en pil på en
// sekvens der rekkefølgen ikke er bestemt. Den returnerer en diskriminert union,
// ikke et tall med et flagg ved siden av: et valgfritt `usikker?`-felt ville blitt
// glemt av den første skjermen som hastet.
import {
  MAKS_FART_TIL_FOTS_MS,
  MAKS_OPPHOLD_MS,
  MAX_UTLOSNINGER,
  haversineM,
  sekvensSpor,
  sensorPos,
  ugsKlassifiser,
  type UgsKlassifisering,
  type UgsSekvens,
  type UgsSensor,
} from '@skade/contracts';
import type { Ullhendelse } from '../api/typer.js';
import { formaterKursSlakk, himmelretning } from './format.js';

// ── Valg og grenser ─────────────────────────────────────────────────────────

/**
 * Taket på hvor langt to utløsninger kan ligge fra hverandre og fortsatt regnes
 * som samme kandidat. UVERIFISERT, og bevisst STORT: et jegerlags sensorfelt er
 * hundrevis av meter, ikke kilometer, så 1,5 km treffer bare de tilfellene der
 * to helt ulike deler av feltet ellers ville blitt blandet. Er taket for lite,
 * deler vi opp en ekte bevegelse; er det for stort, blander vi to. Feilen som
 * koster mest er å DELE en ekte bevegelse, for da forsvinner retningen.
 */
export const MAKS_SPRANG_M = 1500;

/**
 * Over denne kursslarken skal retningen leses som en HIMMELRETNING og ikke som en
 * kurs i grader.
 *
 * 22,5° er halve kompassektoren (kompasset har åtte sektorer à 45°). Er slarken
 * større enn det, spenner retningen en hel sektor eller mer, og da er «mot ØST» et
 * ærligere svar enn «070°» — sifrene ville påstått en presisjon som ikke finnes.
 * UVERIFISERT som terskel, men geometrisk begrunnet.
 */
export const KURS_SLAKK_GROV_DEG = 22.5;

/**
 * Over denne er retningen IKKE bestemt.
 *
 * MERK hvordan kontraktens egen formel oppfører seg, for det er ikke opplagt:
 * `kursSlakkDeg` er `atan(slakk/avstand)` NÅR avstanden er større enn slarken, og
 * hardkodet `180` ellers. Siden `atan(x)` er under 45° for alle x < 1, kan
 * kontrakten bare gi verdier UNDER 45° eller EKSAKT 180 — aldri noe mellom. Denne
 * terskelen fanger altså i praksis 180-tilfellet, og 90 er valgt fordi det er den
 * prinsipielle grensen: en retning som spenner en halvplan er ingen retning.
 * Skulle kontrakten en dag regne slarken annerledes, holder regelen fortsatt.
 */
export const KURS_SLAKK_UBRUKELIG_DEG = 90;

export interface SekvensValgUll {
  /** Maks opphold mellom to utløsninger i samme kandidat (ms). */
  maksOppholdMs?: number;
  /** Maks avstand mellom to påfølgende utløsninger i samme kandidat (m). */
  maksSprangM?: number;
  /** Fysisk maks fart for antatt måltype (m/s). Sendes videre til kontrakten. */
  maksFartMs?: number;
  /** Ekstra klokkeslark som legges på hver utløsning (ms). Til kontrakten. */
  ekstraTidsslakkMs?: number;
}

// ── Resultattyper ───────────────────────────────────────────────────────────

export interface Klassifisert {
  hendelse: Ullhendelse;
  sensor: UgsSensor;
  klasse: UgsKlassifisering;
}

export type Hasteband = 'akutt' | 'folg' | 'logg';

export const HASTEBAND_NAVN: Record<Hasteband, string> = {
  akutt: 'PÅGÅR',
  folg: 'FØLG',
  logg: 'LOGG',
};

export interface Hastebidrag {
  navn: string;
  poeng: number;
}

export interface Haste {
  poeng: number;
  band: Hasteband;
  /** Hvorfor. Vises ved siden av båndet — en rangering ingen kan etterprøve
   *  blir ikke stolt på, og da sorterer jegeren i hodet i stedet. */
  bidrag: Hastebidrag[];
}

export interface Kandidat {
  /** Stabil id: den FØRSTE utløsningens id. Stabil betyr at kortet ikke bytter
   *  identitet når en ny utløsning kommer inn bakerst. */
  id: string;
  /** Utløsningene i tidsrekkefølge. Minst én. */
  hendelser: Ullhendelse[];
  /** Sensorene som er involvert, i den rekkefølgen de utløste. Kan ha gjentak. */
  sensorIder: string[];
  fraMs: number;
  tilMs: number;
  /** Kontraktens svar. `null` når den nektet — se `avslag`. */
  sekvens: UgsSekvens | null;
  /** Kontraktens `arsak` ORDRETT når den nektet. Aldri omskrevet. */
  avslag: string | null;
  /** Hvorfor kandidaten ble skilt fra den forrige. `null` = den er den første. */
  skilleGrunn: string | null;
  klassifiseringer: Klassifisert[];
  /** Den klassifiseringen med høyest konfidens. Aldri en sammenslåing av flere:
   *  et gjennomsnitt av «dyr 0,45» og «person 0,45» er ikke en klasse. */
  beste: Klassifisert | null;
  haste: Haste;
}

export interface Sekvensbilde {
  kandidater: Kandidat[];
  /**
   * Utløsninger som IKKE kan analyseres fordi sensoren mangler posisjon (broen
   * har aldri annonsert noden). De holdes UTENFOR kandidatene — én posisjonsløs
   * utløsning ville ellers fått kontrakten til å avvise hele kandidaten — men de
   * skjules ALDRI. Skjermen skal si at de finnes og hvorfor de ikke kan brukes.
   */
  utenPosisjon: Ullhendelse[];
}

// ── Gruppering ──────────────────────────────────────────────────────────────

export interface Gruppe {
  hendelser: Ullhendelse[];
  skilleGrunn: string | null;
}

function fmt1(n: number): string {
  return n.toFixed(1).replace('.', ',');
}

/**
 * Del utløsningene i kandidat-grupper. REN funksjon — hele grunnen til at den er
 * skilt fra tegningen er at grupperingen er det ene stedet vi TOLKER, og en
 * tolkning uten tester er en tolkning som stille endrer seg.
 *
 * Inndata forventes i tidsrekkefølge (`hendelserITid` i `lib/lager.ts`); den
 * sorterer likevel selv, fordi en usortert liste ellers ville gitt tilfeldige
 * grupper uten at noe klaget.
 */
export function grupper(
  hendelser: readonly Ullhendelse[],
  sensorer: readonly UgsSensor[],
  valg: SekvensValgUll = {},
): Gruppe[] {
  const maksOpphold = valg.maksOppholdMs ?? MAKS_OPPHOLD_MS;
  const maksSprang = valg.maksSprangM ?? MAKS_SPRANG_M;
  const kart = new Map(sensorer.map((s) => [s.sensorId, s]));

  const rekke = [...hendelser].sort(
    (a, b) => a.utlosning.tidspunktMs - b.utlosning.tidspunktMs || a.id.localeCompare(b.id, 'no'),
  );

  const ut: Gruppe[] = [];
  let nå: Ullhendelse[] = [];
  let grunn: string | null = null;

  const avslutt = (nyGrunn: string | null): void => {
    if (nå.length > 0) ut.push({ hendelser: nå, skilleGrunn: grunn });
    nå = [];
    grunn = nyGrunn;
  };

  for (const h of rekke) {
    const forrige = nå[nå.length - 1];
    if (!forrige) {
      nå = [h];
      continue;
    }
    const dtMs = h.utlosning.tidspunktMs - forrige.utlosning.tidspunktMs;
    if (dtMs > maksOpphold) {
      avslutt(
        `Skilt fra forrige kandidat: ${fmt1(dtMs / 1000)} s opphold overstiger ${maksOpphold / 1000} s. `
        + 'To hendelser, ikke én bevegelse — men om det er samme mål som stoppet og gikk videre, kan ingen sensor svare på.',
      );
      nå = [h];
      continue;
    }
    const sa = kart.get(forrige.sensorId);
    const sb = kart.get(h.sensorId);
    if (sa && sb) {
      const d = haversineM(sensorPos(sa), sensorPos(sb));
      if (d > maksSprang) {
        avslutt(
          `Skilt fra forrige kandidat: ${Math.round(d)} m mellom ${sa.sensorId} og ${sb.sensorId} overstiger taket på ${maksSprang} m. `
          + 'Utløsningene er i ulike deler av feltet, og en netto kurs mellom dem ville ikke betydd noe.',
        );
        nå = [h];
        continue;
      }
    }
    if (nå.length >= MAX_UTLOSNINGER) {
      avslutt(
        `Skilt fra forrige kandidat: kontraktens tak på ${MAX_UTLOSNINGER} utløsninger i én analyse er nådd. `
        + 'Kandidaten foran er FULL — den er ikke nødvendigvis ferdig.',
      );
      nå = [h];
      continue;
    }
    nå.push(h);
  }
  avslutt(null);
  return ut;
}

// ── Retning og fart som UTSAGN, ikke som tall ───────────────────────────────

export type Retningsutsagn =
  /** Retningen er bestemt. `grov` = les den som himmelretning, ikke som kurs. */
  | { slag: 'bestemt'; kursDeg: number; slakkDeg: number; grov: boolean; tekst: string }
  /**
   * Rekkefølgen på minst ett bein er IKKE bestemt (|Δt| ≤ klokkeslarken). Da
   * finnes ingen retning — den kan være den motsatte. Ingen pil skal tegnes.
   */
  | { slag: 'usikker-rekkefolge'; usikreBein: number; totaltBein: number; tekst: string }
  /** Geometrien gir ingen retning: samme sensor først og sist, eller
   *  posisjonsslarken er større enn avstanden. */
  | { slag: 'ikke-bestemt'; tekst: string };

/**
 * Kan vi si hvilken vei det gikk?
 *
 * Rekkefølgen på testene er rangert etter hva som ØDELEGGER retningen mest, og
 * den strengeste står først med vilje: er ett eneste bein tidsmessig ubestemt,
 * er ikke kjeden ordnet, og da er netto kurs fra «første» til «siste» et tall
 * regnet på en rekkefølge vi ikke har. Det er lett å tenke at ett usikkert bein
 * midt i en lang kjede er uvesentlig — men vi kan ikke vite hvilket bein som er
 * «midt i» før rekkefølgen er kjent, og det er nettopp den som mangler.
 */
export function retningsutsagn(s: UgsSekvens): Retningsutsagn {
  const usikre = s.bein.filter((b) => !b.rekkefolgeSikker).length;
  if (usikre > 0) {
    return {
      slag: 'usikker-rekkefolge',
      usikreBein: usikre,
      totaltBein: s.bein.length,
      tekst:
        `REKKEFØLGEN ER IKKE BESTEMT på ${usikre} av ${s.bein.length} bein: tiden mellom utløsningene er mindre enn `
        + 'klokkeslarken. Retningen kan være den motsatte, og derfor tegnes ingen pil.',
    };
  }
  if (s.nettoKursDeg === undefined) {
    return {
      slag: 'ikke-bestemt',
      tekst:
        'Ingen netto retning: første og siste utløsning er på SAMME sensor. Tolkninger: målet snudde, målet oppholder seg i '
        + 'dekningsområdet, eller to ulike mål passerte samme punkt.',
    };
  }
  const slakk = s.nettoKursSlakkDeg ?? 180;
  if (slakk >= KURS_SLAKK_UBRUKELIG_DEG) {
    return {
      slag: 'ikke-bestemt',
      tekst:
        `Retningen er IKKE bestemt: kursslarken er ±${Math.round(slakk)}°, altså minst en halvplan. Sensorene står for tett `
        + 'i forhold til deteksjonsradiene sine for at retningen mellom dem kan måles.',
    };
  }
  const grov = slakk >= KURS_SLAKK_GROV_DEG;
  return {
    slag: 'bestemt',
    kursDeg: s.nettoKursDeg,
    slakkDeg: slakk,
    grov,
    tekst: grov
      ? `Grov retning: mot ${himmelretning(s.nettoKursDeg)} (${formaterKursSlakk(s.nettoKursDeg, slakk)}). Slarken er så stor at himmelretningen er det ærlige svaret.`
      : `Retning ${formaterKursSlakk(s.nettoKursDeg, slakk)}.`,
  };
}

// ── Klassifisering av kandidatens utløsninger ──────────────────────────────

/**
 * Klassifiser hver utløsning for seg, med kontraktens `ugsKlassifiser()`.
 *
 * MERK at vi IKKE slår dem sammen til én klasse med én konfidens. Det ville vært
 * den vanligste feilen i et slikt UI: tre seismiske utløsninger som hver sier
 * «person 0,45» blir ikke «person 0,9» av å legges sammen — de kan alle tre være
 * den samme elgen, og elgen gir sterkere utslag enn en person. Kontrakten kapper
 * derfor seismisk konfidens på 0,45, og vi respekterer taket ved å vise den
 * STERKESTE enkeltklassifiseringen og la resten stå som egne rader.
 */
export function klassifiser(
  hendelser: readonly Ullhendelse[],
  kart: ReadonlyMap<string, UgsSensor>,
): Klassifisert[] {
  const ut: Klassifisert[] = [];
  for (const h of hendelser) {
    const sensor = kart.get(h.sensorId);
    if (!sensor) continue;
    ut.push({ hendelse: h, sensor, klasse: ugsKlassifiser(h.utlosning, sensor) });
  }
  return ut;
}

/** Den sterkeste enkeltklassifiseringen. `menneske`-grunnlag vinner alltid. */
export function besteKlassifisering(k: readonly Klassifisert[]): Klassifisert | null {
  let beste: Klassifisert | null = null;
  for (const x of k) {
    if (!beste) { beste = x; continue; }
    const menneskeNå = x.klasse.grunnlag === 'menneske';
    const menneskeFør = beste.klasse.grunnlag === 'menneske';
    if (menneskeNå !== menneskeFør) {
      if (menneskeNå) beste = x;
      continue;
    }
    if (x.klasse.konfidens > beste.klasse.konfidens) beste = x;
  }
  return beste;
}

// ── Hastegrad: hva krever oppmerksomhet NÅ ─────────────────────────────────

/** Under dette er hendelsen kanskje ikke over ennå. UVERIFISERT. */
export const FERSK_MS = 120_000;
/** Under dette er den fortsatt et situasjonsbilde. UVERIFISERT. */
export const NYLIG_MS = 900_000;
/** Over dette er den logg, ikke situasjonsbilde. */
export const HISTORIKK_MS = 3_600_000;

export const HASTE_TERSKEL_AKUTT = 70;
export const HASTE_TERSKEL_FOLG = 35;

/**
 * Hva som krever oppmerksomhet NÅ.
 *
 * VEKTENE ER UVERIFISERTE og de er et OPERATIVT valg, ikke en måling — samme
 * status som NJORDs trusselvekter. De er valgt etter én regel: rangeringen skal
 * belønne det som fortsatt kan handles på, og straffe det som allerede er over.
 *
 *  * FERSKHET veier mest (50). En sekvens der siste utløsning er to minutter
 *    gammel pågår kanskje nå; en fra i går kan ingen gjøre noe med, uansett hvor
 *    interessant den er. Dette er det motsatte av NJORD, der en kontakt ligger på
 *    kartet og kan følges — en UGS-utløsning er over i det den er meldt.
 *  * FLERE UAVHENGIGE NODER (8 per node, tak 24). Én node kan feilutløse på vind i
 *    et tre; tre noder i rekkefølge er vanskeligere å forklare bort.
 *  * MENNESKEVURDERT BILDE (25) er det største enkeltbidraget etter ferskhet, og
 *    det er hele poenget med kontraktens konfidenstak: alt annet i denne appen er
 *    en slutningskjede, dette er en OBSERVASJON.
 *  * KJØRETØY (15) over LØPENDE PERSON (10) over GÅENDE (5): et kjøretøy på en
 *    skogsvei bak fiendens linjer er en annen sak enn en person.
 *  * BARE DYR (−20). Ikke null, og ikke utelatelse: en elg som utløser tre noder
 *    er verdt å vite om, fordi den forklarer hvorfor de utløste.
 *  * UMULIG FART (+10). «Minst to ulike mål ELLER klokkefeil» er begge verdt et
 *    blikk — den ene er flere aktører enn du trodde, den andre gjør ALLE
 *    fartstallene dine feil.
 *  * JEGERENS EGET FLAGG slår alt: «relevant» +40, og «avskrevet» senker
 *    kandidaten under alt annet (−1000). Et menneskes vurdering skal ikke kunne
 *    konkurrere med en heuristikk.
 */
export function hastegrad(
  hendelser: readonly Ullhendelse[],
  sekvens: UgsSekvens | null,
  klassifiseringer: readonly Klassifisert[],
  tilMs: number,
  naaMs: number,
): Haste {
  const bidrag: Hastebidrag[] = [];
  const alder = Math.max(0, naaMs - tilMs);

  if (alder < FERSK_MS) {
    bidrag.push({ navn: 'Siste utløsning under 2 min gammel — hendelsen kan pågå nå', poeng: 50 });
  } else if (alder < NYLIG_MS) {
    bidrag.push({ navn: 'Siste utløsning under 15 min gammel', poeng: 30 });
  } else if (alder < HISTORIKK_MS) {
    bidrag.push({ navn: 'Siste utløsning under en time gammel', poeng: 12 });
  } else {
    bidrag.push({ navn: 'Over en time gammel — logg, ikke situasjonsbilde', poeng: 0 });
  }

  const noder = new Set(hendelser.map((h) => h.sensorId)).size;
  if (noder > 1) {
    bidrag.push({ navn: `${noder} uavhengige noder har utløst`, poeng: Math.min(24, (noder - 1) * 8) });
  }

  if (klassifiseringer.some((k) => k.klasse.grunnlag === 'menneske')) {
    bidrag.push({ navn: 'Et MENNESKE har sett et bilde fra sekvensen', poeng: 25 });
  }

  const klasser = new Set(klassifiseringer.map((k) => k.klasse.klasse));
  if (klasser.has('hjul') || klasser.has('belte')) {
    bidrag.push({ navn: 'Kjøretøysignatur i sekvensen', poeng: 15 });
  } else if (klasser.has('person-lopende')) {
    bidrag.push({ navn: 'Løpende person i sekvensen', poeng: 10 });
  } else if (klasser.has('person-til-fots')) {
    bidrag.push({ navn: 'Person til fots i sekvensen', poeng: 5 });
  }

  if (klassifiseringer.length > 0 && klasser.size === 1 && klasser.has('dyr')) {
    bidrag.push({ navn: 'Alle klassifiseringer peker på DYR', poeng: -20 });
  }

  const umulige = sekvens?.bein.filter((b) => b.urimeligFart).length ?? 0;
  if (umulige > 0) {
    bidrag.push({ navn: `${umulige} bein med fysisk umulig fart — to ulike mål, eller klokkefeil`, poeng: 10 });
  }

  if (hendelser.some((h) => h.flagg === 'relevant')) {
    bidrag.push({ navn: 'Merket RELEVANT av jegeren', poeng: 40 });
  }
  if (hendelser.length > 0 && hendelser.every((h) => h.flagg === 'avskrevet-dyr' || h.flagg === 'avskrevet-egen')) {
    bidrag.push({ navn: 'Avskrevet av jegeren', poeng: -1000 });
  }

  const poeng = bidrag.reduce((s, b) => s + b.poeng, 0);
  const band: Hasteband = poeng >= HASTE_TERSKEL_AKUTT ? 'akutt' : poeng >= HASTE_TERSKEL_FOLG ? 'folg' : 'logg';
  return { poeng, band, bidrag };
}

// ── Hele analysen ───────────────────────────────────────────────────────────

/**
 * Grupper, analyser og rangér. Dette er den ENE funksjonen hovedskjermen kaller.
 *
 * Rekkefølgen på det som skjer er ikke tilfeldig: grupperingen er vår tolkning,
 * kontraktens `sekvensSpor()` er regnestykket, og hastegraden er en rangering på
 * toppen. Blandet sammen ville det vært umulig å si hvem som tok hvilken
 * beslutning når et tall ser feil ut.
 */
export function analyser(
  hendelser: readonly Ullhendelse[],
  sensorer: readonly UgsSensor[],
  naaMs: number,
  valg: SekvensValgUll = {},
): Sekvensbilde {
  const kart = new Map(sensorer.map((s) => [s.sensorId, s]));
  const utenPosisjon = hendelser.filter((h) => !kart.has(h.sensorId));
  const brukbare = hendelser.filter((h) => kart.has(h.sensorId));

  const kandidater: Kandidat[] = [];
  for (const g of grupper(brukbare, sensorer, valg)) {
    const utl = g.hendelser.map((h) => h.utlosning);
    const svar = sekvensSpor(utl, [...sensorer], {
      ...(valg.maksOppholdMs !== undefined ? { maksOppholdMs: valg.maksOppholdMs } : {}),
      ...(valg.maksFartMs !== undefined ? { maksFartMs: valg.maksFartMs } : { maksFartMs: MAKS_FART_TIL_FOTS_MS }),
      ...(valg.ekstraTidsslakkMs !== undefined ? { ekstraTidsslakkMs: valg.ekstraTidsslakkMs } : {}),
    });
    const klassifiseringer = klassifiser(g.hendelser, kart);
    const fraMs = g.hendelser[0].utlosning.tidspunktMs;
    const tilMs = g.hendelser[g.hendelser.length - 1].utlosning.tidspunktMs;
    const sekvens = svar.ok ? svar.sekvens : null;
    kandidater.push({
      id: g.hendelser[0].id,
      hendelser: g.hendelser,
      sensorIder: g.hendelser.map((h) => h.sensorId),
      fraMs,
      tilMs,
      sekvens,
      // Kontraktens egen ordlyd, ORDRETT. Den forklarer premisset for hele appen
      // bedre enn noe vi kunne skrevet her: én utløsning er et punkt.
      avslag: svar.ok ? null : svar.arsak,
      skilleGrunn: g.skilleGrunn,
      klassifiseringer,
      beste: besteKlassifisering(klassifiseringer),
      haste: hastegrad(g.hendelser, sekvens, klassifiseringer, tilMs, naaMs),
    });
  }

  return { kandidater: sorterKandidater(kandidater), utenPosisjon };
}

/**
 * Rangér kandidatene. STABIL sortering: like poeng brytes på tid, og lik tid på
 * id. En liste som stokker seg selv hvert sekund er umulig å peke på, og det er
 * en ekte brukbarhetsfeil — ikke en skjønnhetsfeil.
 */
export function sorterKandidater(k: readonly Kandidat[]): Kandidat[] {
  return [...k].sort(
    (a, b) => b.haste.poeng - a.haste.poeng || b.tilMs - a.tilMs || a.id.localeCompare(b.id, 'no'),
  );
}

// ── Kjeden som LEDD: gjentak slås sammen, tilbakevending merkes ─────────────

/**
 * Ett ledd i kjedestripa. Rapportens funn 6: samme node sto to ganger PÅ RAD i
 * kjeden, uten forklaring — det leses som en bug («hvorfor står den der to
 * ganger?»), og en stripe man ikke stoler på leses ikke.
 */
export interface Kjedeledd {
  sensorId: string;
  /** Antall utløsninger PÅ RAD på denne noden, slått sammen til ett ledd (≥1). */
  antall: number;
  /**
   * Noden har allerede stått TIDLIGERE i kjeden, med andre noder imellom. Det
   * er en legitim TILBAKEVENDING — målet kan ha snudd, eller det er flere mål —
   * og den kan ikke slås sammen: rekkefølgen er informasjonen. Merkes i stedet.
   */
  tilbakevending: boolean;
}

/**
 * Sanity-sjekken for kjedestripa. REN funksjon med tester, fordi skillet er
 * lett å rote til: samme node to ganger PÅ RAD er én node som utløste på nytt
 * (ett ledd, telt), mens samme node IGJEN etter andre noder er en bevegelse
 * tilbake (eget ledd, merket). Beina i kontrakten røres ikke — de viser
 * fortsatt hvert par, med `sammeSensor` der det gjelder.
 */
export function kjedeledd(sensorIder: readonly string[]): Kjedeledd[] {
  const ut: Kjedeledd[] = [];
  for (const id of sensorIder) {
    const siste = ut[ut.length - 1];
    if (siste && siste.sensorId === id) {
      siste.antall++;
      continue;
    }
    ut.push({ sensorId: id, antall: 1, tilbakevending: ut.some((l) => l.sensorId === id) });
  }
  return ut;
}

// ── Vurderingshistorikk: en vurdering som endres skal SI det ────────────────
//
// Rapportens funn: retningen sto som «mot ØST» i én tegning og «UBESTEMT» i
// neste, uten et ord om hvorfor. Det ER riktig oppførsel — en ny utløsning kan
// svekke geometrien — men en vurdering som endrer seg stille leses som en bug,
// og da slutter jegeren å stole på HELE analysen. Endringen skal derfor stå
// som en linje på kortet, med tidspunkt.

/**
 * De avledede vurderingene som kan endre seg mellom to beregninger, kokt ned
 * til det som skal sammenlignes. `retning` er himmelretningen når den er
 * bestemt, ellers `null` (= UBESTEMT) — grader sammenlignes ikke, fordi en
 * drift fra 087° til 091° ikke er en ENDRING i det jegeren ble fortalt.
 */
export interface Sekvensvurdering {
  retning: string | null;
  bein: number;
  antattPasserende: number;
}

/** Kandidatens vurdering, eller `null` når kontrakten nektet (ett punkt). */
export function vurderingAv(k: Kandidat): Sekvensvurdering | null {
  if (!k.sekvens) return null;
  const r = retningsutsagn(k.sekvens);
  return {
    retning: r.slag === 'bestemt' ? himmelretning(r.kursDeg) : null,
    bein: k.sekvens.bein.length,
    antattPasserende: k.sekvens.antallPasserendeAntatt,
  };
}

/**
 * Én kort norsk linje om hva som endret seg — eller `null` når ingenting
 * gjorde det. Ordvalget «nedjustert» er bevisst: å miste retningen er å miste
 * informasjon, og det skal ikke pakkes inn som en nøytral «endring». Årsaken
 * påstås IKKE mer presist enn vi vet — «kjeden bærer ingen retning lenger» er
 * sant både for svekket geometri og for ubestemt rekkefølge.
 */
export function diffVurdering(før: Sekvensvurdering, nå: Sekvensvurdering): string | null {
  const deler: string[] = [];
  if (før.retning !== nå.retning) {
    if (før.retning !== null && nå.retning === null) {
      deler.push(`retning nedjustert fra ${før.retning} til UBESTEMT — kjeden bærer ingen retning lenger`);
    } else if (før.retning === null && nå.retning !== null) {
      deler.push(`retning bestemt til ${nå.retning} — var UBESTEMT`);
    } else {
      deler.push(`retning endret fra ${før.retning} til ${nå.retning}`);
    }
  }
  if (før.bein !== nå.bein) {
    deler.push(`kjeden ${nå.bein > før.bein ? 'vokste' : 'krympet'} fra ${før.bein} til ${nå.bein} bein`);
  }
  if (før.antattPasserende !== nå.antattPasserende) {
    deler.push(`antatt passerende endret fra ${før.antattPasserende} til ${nå.antattPasserende}`);
  }
  if (deler.length === 0) return null;
  const tekst = deler.join(' · ');
  return tekst.charAt(0).toUpperCase() + tekst.slice(1);
}

export interface Vurderingsendring {
  tekst: string;
  tsMs: number;
}

/** Sporet per kandidat: forrige vurdering, og den siste endringen som skjedde. */
export interface Vurderingsspor {
  vurdering: Sekvensvurdering | null;
  sisteEndring: Vurderingsendring | null;
}

/**
 * Før minnet ett tegn videre. REN: tar forrige minne og dagens kandidater,
 * returnerer et NYTT minne — muterer aldri inn. Kandidater som er borte,
 * forsvinner fra minnet (det er VIEW-minne, ikke logg), og en endringslinje
 * blir stående til en NY endring erstatter den — en linje som forsvant etter
 * ett sekund ville aldri blitt lest.
 */
export function oppdaterVurderingsminne(
  minne: ReadonlyMap<string, Vurderingsspor>,
  kandidater: readonly Kandidat[],
  naaMs: number,
): Map<string, Vurderingsspor> {
  const ut = new Map<string, Vurderingsspor>();
  for (const k of kandidater) {
    const nå = vurderingAv(k);
    const før = minne.get(k.id);
    let sisteEndring = før?.sisteEndring ?? null;
    if (før?.vurdering && nå) {
      const d = diffVurdering(før.vurdering, nå);
      if (d) sisteEndring = { tekst: d, tsMs: naaMs };
    }
    ut.set(k.id, { vurdering: nå, sisteEndring });
  }
  return ut;
}

/**
 * Én linje som oppsummerer kandidaten, til lister og panel-undertekster.
 *
 * Den sier ALDRI en fart eller en retning uten forbeholdet — hele hensikten med
 * en kort form er at den skal kunne leses alene, og et sammendrag som utelater
 * usikkerheten er verre enn ingen sammendrag.
 */
export function sammendrag(k: Kandidat): string {
  const noder = new Set(k.sensorIder).size;
  const antall = `${k.hendelser.length} utløsning${k.hendelser.length === 1 ? '' : 'er'} på ${noder} node${noder === 1 ? '' : 'r'}`;
  if (!k.sekvens) return `${antall} — kan ikke analyseres som sekvens`;
  const r = retningsutsagn(k.sekvens);
  const retning =
    r.slag === 'bestemt'
      ? `mot ${himmelretning(r.kursDeg)}${r.grov ? ' (grovt)' : ''}`
      : r.slag === 'usikker-rekkefolge'
        ? 'retning UBESTEMT (rekkefølge)'
        : 'retning UBESTEMT (geometri)';
  const ant = k.sekvens.antallPasserendeAntatt;
  return `${antall} · ${retning} · antatt ${ant} passerende`;
}
